import {useEffect, useMemo, useRef, useState} from "react";
import {Search} from "lucide-react";

import VideoStats from "../components/VideoStats";
import VideoGrid from "../components/VideoGrid";
import CatalogBrowser from "../components/CatalogBrowser";
import {buildCatalog} from "../catalog/buildCatalog";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import useActivity from "../hooks/useActivity";
import useWatchHistory from "../hooks/useWatchHistory";
import {useStorage} from "../hooks/useStorage";
import {useSync} from "../hooks/useSync";
import {useVideos} from "../hooks/useVideos";

const VideoDashboard = ({
  user,
  recentVideos,
  filteredQuery,
  onWatchHistoryChange,
}) => {
  // The single useStorage instance for the dashboard. useActivity and
  // useWatchHistory each used to call useStorage themselves; the two
  // instances shared one sessionStorage key and each wrote the full
  // {activity, watchHistory} object, so whichever debounced write fired
  // last clobbered the other slice with a stale copy.
  const {activity, watchHistory, setActivity, setWatchHistory} = useStorage({
    user,
  });
  const {activityRef} = useActivity({user, activity, setActivity});
  const {historyRef} = useWatchHistory({user, watchHistory, setWatchHistory});
  const {videos, filtered, isLoading} = useVideos({
    filteredQuery,
    watchHistory,
  });

  // Section -> Playlist -> ordered videos, derived from the catalog rows.
  const catalog = useMemo(() => buildCatalog(videos), [videos]);
  const {updateDailyStat, handleVideoProgress} = useSync({
    user,
    activity,
    activityRef,
    setActivity,
    watchHistory,
    historyRef,
    setWatchHistory,
  });

  // Round-3 #7: Recently Watched lived on App but only sourced from a
  // snapshot of `loginState.user.watch_history` taken at login. Plays
  // never reached App, so the sidebar stayed frozen. We forward live
  // watchHistory updates back up so App can recompute the sidebar in
  // a single useMemo.
  useEffect(() => {
    if (!onWatchHistoryChange) return;
    onWatchHistoryChange(watchHistory);
  }, [watchHistory, onWatchHistoryChange]);

  // Round-4: the search box lives in the top header, but the matching
  // videos render in the grid far below the fold (banner + stats +
  // sort + filter sit above it). A user searching from the top saw no
  // change and assumed nothing matched. When a search term is active,
  // scroll the results section into view so the feedback is immediate.
  const activeQuery = (filteredQuery || "").trim();
  // True while CatalogBrowser shows a "See all" playlist detail; the header
  // (tagline + progress card) hides so "Back" sits at the top of the page.
  const [playlistViewOpen, setPlaylistViewOpen] = useState(false);
  const resultsRef = useRef(null);
  // The grid is virtualized against this element, so it needs a ref to it.
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    if (!activeQuery) return;
    resultsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
  }, [activeQuery]);

  // While the catalog is still loading, show a clean blank screen with
  // just the spinner instead of rendering the dashboard chrome over an
  // empty grid. Keeps the initial load from looking broken/janky.
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-white">
        <OrbitLoader />
      </div>
    );
  }

  return (
    /* This div -- not the outer <main> -- is the element that actually
       scrolls the learner grid, so overscroll-contain belongs here: it
       stops the scroll leaking to the page and the mobile rubber-band
       bounce at the top/bottom of the (very long) video list. It is also
       the scroll container the virtualized VideoGrid measures against. */
    <div
      ref={scrollContainerRef}
      className="h-full w-full p-4 md:p-8 pb-[calc(4rem+env(safe-area-inset-bottom))] font-sans bg-white overflow-y-auto overscroll-contain relative"
    >
      {!playlistViewOpen && <VideoStats {...{activity, watchHistory}} />}

      <section ref={resultsRef} className="mb-6 scroll-mt-6">
        {activeQuery ? (
          <>
            {/* Search: a flat result grid across the whole catalog, with
                explicit feedback on the active term and match count. */}
            <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
              <Search size={16} className="text-blue-500 shrink-0" />
              <span>
                {filtered.length === 0 ? (
                  <>
                    No videos match{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-slate-800">
                      {filtered.length}
                    </span>{" "}
                    {filtered.length === 1 ? "result" : "results"} for{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                )}
              </span>
            </div>
            <VideoGrid
              videos={filtered}
              scrollRef={scrollContainerRef}
              {...{
                recentVideos,
                watchHistory,
                handleVideoProgress,
                updateDailyStat,
              }}
            />
          </>
        ) : (
          // Browse: Section -> Playlist -> ordered videos.
          <CatalogBrowser
            catalog={catalog}
            onSeeAllActiveChange={setPlaylistViewOpen}
            {...{
              recentVideos,
              watchHistory,
              handleVideoProgress,
              updateDailyStat,
            }}
          />
        )}
      </section>
    </div>
  );
};

export default VideoDashboard;
